<template>
  <!-- Sheet on top of the stack (see detailStack.js); the ones below are
       loaded again in a moment when coming back to them -->
  <template v-if="top">
    <OfferQuickPanel
      v-if="top.type === 'offer'"
      ref="sheet"
      :key="`offer-${top.id}`"
      visible
      :z-index="Z_INDEX"
      :offer="state.offer"
      :loading="state.loading"
      :show-ownership="Boolean(state.offer.owns)"
      :ownership-loading="state.busy"
      :back-label="backLabel"
      @back="backDetail"
      @close="closeDetails"
      @refresh="loadOffer"
      @toggle-ownership="toggleOwnership"
      @price-updated="(fields) => Object.assign(state.offer, fields)"
      @open-acquisition="(offer) => openDetail('purchase', offer._key)"
      @open-package="openPackage"
      @unfollow="unfollow"
    />
    <AcquisitionQuickPanel
      v-else-if="top.type === 'purchase'"
      ref="sheet"
      :key="`purchase-${top.id}`"
      visible
      :z-index="Z_INDEX"
      :offer="state.offer"
      :offer-loading="state.loading"
      :items="state.items"
      :items-loading="state.itemsLoading"
      :items-loaded="state.itemsLoaded"
      :items-error="state.itemsError"
      :back-label="backLabel"
      @back="backDetail"
      @close="closeDetails"
      @refresh="loadPurchase"
      @edit="(offer) => leaveTo(`/myoffers?offerId=${offer._key}&edit=1`)"
      @manage-items="(offer) => leaveTo(`/myoffers/${offer._key}/items`)"
      @tab-change="purchaseTab"
      @open-item="(key) => openDetail('item', key)"
      @open-package="openPackage"
    />
    <ItemQuickPanel
      v-else-if="top.type === 'item'"
      ref="sheet"
      :key="`item-${top.id}`"
      visible
      :z-index="Z_INDEX"
      :item-id="top.id"
      :back-label="backLabel"
      @back="backDetail"
      @close="closeDetails"
      @saved-item="(item) => notifyChanged('item', item)"
      @open-offer="(key) => openDetail('purchase', key)"
      @open-package="openPackage"
      @open-sale="(key) => openDetail('sale', key)"
    />
    <SaleQuickPanel
      v-else-if="top.type === 'sale'"
      ref="sheet"
      :key="`sale-${top.id}`"
      visible
      :z-index="Z_INDEX"
      :sale="state.sale"
      :back-label="backLabel"
      @back="backDetail"
      @close="closeDetails"
      @edit="(sale) => leaveTo(`/sales?sale=${sale._key}&edit=1`)"
      @delete="deleteSale"
      @open-item="(key) => openDetail('item', key)"
    />
  </template>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import axios from "axios";
import config from "@/config/config";
import { dropQueryParams } from "../../urlQuery";
import {
  DETAIL_LABELS,
  backDetail,
  closeDetails,
  detailStack,
  navigateFromDetail,
  notifyChanged,
  openDetail,
  parseOpenParam,
  takeSnapshot,
} from "../../detailStack";
import AcquisitionQuickPanel from "../offers/AcquisitionQuickPanel.vue";
import ItemQuickPanel from "../items/ItemQuickPanel.vue";
import OfferQuickPanel from "../offers/OfferQuickPanel.vue";
import SaleQuickPanel from "../sales/SaleQuickPanel.vue";

// Above the sheets the pages open themselves
const Z_INDEX = 1400;

export default {
  name: "DetailHost",
  components: {
    AcquisitionQuickPanel,
    ItemQuickPanel,
    OfferQuickPanel,
    SaleQuickPanel,
  },
  setup() {
    const route = useRoute();
    const top = computed(
      () => detailStack.entries[detailStack.entries.length - 1] || null,
    );
    const backLabel = computed(() =>
      top.value?.from ? `Retour à ${DETAIL_LABELS[top.value.from]}` : "",
    );

    const blank = () => ({
      offer: {},
      sale: {},
      loading: false,
      busy: false,
      items: [],
      itemsLoading: false,
      itemsLoaded: false,
      itemsError: "",
    });
    const state = reactive(blank());
    let request = 0;

    const loadOffer = async () => {
      const entry = top.value;
      const id = ++request;
      state.loading = true;
      try {
        const { data } = await axios.get(`${config.apiUrl}/offers/${entry.id}`);
        if (id === request) state.offer = { _key: entry.id, ...data };
      } catch (error) {
        console.error("Fiche indisponible", error);
      } finally {
        if (id === request) state.loading = false;
      }
    };
    const loadPurchase = loadOffer;
    const purchaseTab = async (tab) => {
      if (tab !== "content" || state.itemsLoaded || state.itemsLoading) return;
      const entry = top.value;
      // Another sheet shown meanwhile: its own content, not this one
      const id = request;
      state.itemsLoading = true;
      try {
        const { data } = await axios.get(
          `${config.apiUrl}/offers/${entry.id}/items`,
        );
        if (id !== request) return;
        state.items = Array.isArray(data)
          ? data
          : data?.result || data?.items || [];
        state.itemsLoaded = true;
      } catch (error) {
        if (id === request)
          state.itemsError =
            "Le contenu détaillé est momentanément indisponible.";
      } finally {
        if (id === request) state.itemsLoading = false;
      }
    };
    const loadSale = async () => {
      const entry = top.value;
      const id = ++request;
      state.sale = { _key: entry.id };
      try {
        const { data } = await axios.get(`${config.apiUrl}/sales/${entry.id}`);
        if (id === request)
          state.sale = data?.result || data || { _key: entry.id };
      } catch (error) {
        console.error("Vente indisponible", error);
      }
    };

    // Each sheet shown is loaded (again when coming back to it)
    watch(
      top,
      (entry) => {
        Object.assign(state, blank());
        request++;
        if (!entry) return;
        if (entry.type === "offer" || entry.type === "purchase") loadOffer();
        if (entry.type === "sale") loadSale();
      },
      { immediate: true },
    );

    const leaveTo = (to) => navigateFromDetail(to);
    const openPackage = (packageId) => {
      if (packageId) navigateFromDetail(`/packages/${packageId}`);
    };

    const toggleOwnership = async (offer) => {
      if (
        offer.owns &&
        !window.confirm(
          "Retirer cette annonce de vos achats ? Ses objets et ses coûts restent enregistrés.",
        )
      )
        return;
      state.busy = true;
      try {
        if (offer.owns)
          await axios.delete(`${config.apiUrl}/offers/${offer._key}/owns`);
        else await axios.post(`${config.apiUrl}/offers/${offer._key}/owns`, {});
        notifyChanged("offer", { _key: offer._key, owns: !offer.owns });
        await loadOffer();
      } finally {
        state.busy = false;
      }
    };
    const unfollow = async (offer) => {
      if (!window.confirm("Ne plus suivre cette annonce ?")) return;
      try {
        await axios.delete(`${config.apiUrl}/offers/${offer._key}/follow`);
        notifyChanged("offer", { _key: offer._key, followed: false });
        backDetail();
      } catch {
        window.alert("L’annonce n’a pas pu être retirée.");
      }
    };
    const deleteSale = async (sale) => {
      if (
        !window.confirm(
          `Supprimer la vente « ${sale.title || "sans titre"} » ?`,
        )
      )
        return;
      try {
        await axios.delete(`${config.apiUrl}/sales/${sale._key}`);
        notifyChanged("sale");
        backDetail();
      } catch {
        window.alert("La vente n’a pas pu être supprimée.");
      }
    };

    // Direct links (?open=item:123) and sheets left for another page
    const restore = () => {
      const snapshot = takeSnapshot();
      if (snapshot) {
        detailStack.entries.splice(
          0,
          detailStack.entries.length,
          ...snapshot.map((entry, index) => ({
            ...entry,
            from: index ? snapshot[index - 1].type : entry.from || null,
          })),
        );
        return;
      }
      const requested = parseOpenParam(route.query.open);
      if (requested.length) {
        closeDetails();
        requested.forEach((entry) => openDetail(entry.type, entry.id));
        dropQueryParams("open");
      }
    };
    watch(
      () => route.fullPath,
      (path, previous) => {
        // Another page: the sheets of the previous one are not kept on screen
        if (previous !== undefined) closeDetails();
        restore();
      },
      { immediate: true },
    );

    // Escape: only the sheet on top reacts (the page's own sheet stays open).
    // The sheet decides (requestBack / requestClose): a sheet being edited
    // asks before its changes are lost
    const sheet = ref(null);
    const onKey = (event) => {
      if (event.key !== "Escape" || !top.value) return;
      if (document.querySelector(".image-viewer, .purchase-viewer")) return;
      event.stopImmediatePropagation();
      event.preventDefault();
      if (top.value.from) (sheet.value?.requestBack || backDetail)();
      else (sheet.value?.requestClose || closeDetails)();
    };
    onMounted(() => window.addEventListener("keydown", onKey, true));
    onBeforeUnmount(() => window.removeEventListener("keydown", onKey, true));

    return {
      sheet,
      Z_INDEX,
      backDetail,
      backLabel,
      closeDetails,
      deleteSale,
      leaveTo,
      loadOffer,
      loadPurchase,
      notifyChanged,
      openDetail,
      openPackage,
      purchaseTab,
      state,
      toggleOwnership,
      top,
      unfollow,
    };
  },
};
</script>
